import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { observer } from 'mobx-react';
import { DropdownModelType, useMst } from '../../../internal';
import { Button } from '../../../components/UI/buttons/regular/Button';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { DropdownWithSearch } from '../../../components/UI/dropdown/dropdown-with-search/DropdownWithSearch';
import styles from './RecommendedBrands.module.scss';

interface SortableItemProps {
  id: string;
  index: number;
  model: any;
  detachModel: (model: any) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  index,
  model,
  detachModel,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 0.2s ease-in-out',
  };

  const isDefault = model.value.includes('defaultOption');
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={styles.DropdownItem}
    >
      <div className={styles.ItemNumber}>{index + 1}</div>
      <DropdownWithSearch model={model} />

      <div className={styles.moveIcon} {...listeners} />
      {!isDefault && (
        <div className={styles.deleteIcon} onClick={() => detachModel(model)} />
      )}
    </div>
  );
};

export const RecommendedBrands = observer((): React.ReactElement => {
  const { t } = useTranslation();
  const mst = useMst();
  const { currentPage } = mst;

  const addDropdownItemsCount =
    currentPage.totalNumberOfFields - currentPage.dropdowns.length;
  const addDropdownItems = new Array(addDropdownItemsCount).fill(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const parsedDropdowns = JSON.parse(JSON.stringify(currentPage.dropdowns));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      currentPage.swapDropdowns(active.id, over.id);
    }
  };

  return (
    <div className={styles.PageContainer}>
      <div className={styles.PageTitleContainer}>
        <div className={styles.PageTitle}>
          {t('navigation:recommended_brands')}
        </div>
      </div>
      <div className={styles.ItemsContainer}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={parsedDropdowns}
            strategy={rectSortingStrategy}
          >
            {currentPage.dropdowns.map(
              (dropdown: DropdownModelType, index: number) => {
                return (
                  <SortableItem
                    key={dropdown.id}
                    id={dropdown.id}
                    index={index}
                    model={dropdown}
                    detachModel={currentPage.detachModel}
                  />
                );
              }
            )}
          </SortableContext>
          {addDropdownItems.map((_: any, index: number) => {
            const addDropdownId = `AddDropdown-${index}`;
            return (
              <div key={addDropdownId} className={styles.AddDropdownItemHolder}>
                <div className={styles.ItemNumber}>
                  {currentPage.dropdowns.length + index + 1}
                </div>
                <div
                  onClick={() => currentPage.addDropdown()}
                  className={styles.AddDropdownItem}
                >
                  <p>+ Dodaj brend</p>
                </div>
              </div>
            );
          })}
        </DndContext>
      </div>

      <div className={styles.SaveButtonContainer}>
        <Button model={currentPage.getSaveButton()} />
      </div>
    </div>
  );
});
